/**
 * The event log: what the edge saw on the wire (associations, worklist queries, stores, TLS handshakes, stream and
 * agent events), newest first. Filters by level, source, kind and free text; Live polls for newer events every 2.5 s
 * and prepends them; Pause holds new rows back while you read; Copy puts the visible rows on the clipboard as plain
 * text for a support ticket.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { keyframes } from "@emotion/react";
import {
  Alert, Box, Button, InputAdornment, MenuItem, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CaretDown, CaretRight, ClipboardText, ListMagnifyingGlass, MagnifyingGlass, Pause, Play } from "@phosphor-icons/react";
import { EventLevel, EventPage, EventSource, http, isNotFound, JupiterEvent } from "../api";
import { useSession } from "../AuthToken";
import { ConnectionDots, Dot, Mono } from "../ui";
import { caps, fonts, tones } from "../theme";
import { clockMs, copyText, localDate, NotYet, Panel } from "./common";

const POLL_MS = 2500;
const MAX_ROWS = 2000;
const SOURCES: EventSource[] = ["dicom", "tls", "stream", "agent", "gate"];
const KINDS = ["association", "find", "worklist", "store", "echo", "commitment", "tls", "stream", "config", "upload"];
const levelColor: Record<EventLevel, string> = { info: "rgba(236,233,228,0.55)", warn: tones.warn, error: tones.fail };

const flash = keyframes`
  0% { background-color: ${alpha(tones.live, 0.16)}; }
  100% { background-color: transparent; }
`;

type Filters = { level: "" | EventLevel; source: "" | EventSource; kind: string; q: string };

export function EventLog({ deviceId, nodeId, title = "Event log", maxHeight = 520 }: { deviceId?: string; nodeId?: string; title?: string; maxHeight?: number }) {
  const { token } = useSession();
  const [filters, setFilters] = useState<Filters>({ level: "", source: "", kind: "", q: "" });
  const [q, setQ] = useState("");
  const [items, setItems] = useState<JupiterEvent[]>([]);
  const [held, setHeld] = useState<JupiterEvent[]>([]);
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [error, setError] = useState("");
  const [live, setLive] = useState(true);
  const [paused, setPaused] = useState(false);
  const [copied, setCopied] = useState(false);
  const cursor = useRef<string | null>(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  // Debounce the free-text filter so typing does not fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.q === q ? f : { ...f, q })), 350);
    return () => clearTimeout(t);
  }, [q]);

  const query = useCallback(
    (after?: string | null) => {
      const p = new URLSearchParams();
      if (deviceId) p.set("deviceId", deviceId);
      if (nodeId) p.set("nodeId", nodeId);
      if (filters.level) p.set("level", filters.level);
      if (filters.kind) p.set("kind", filters.kind);
      if (filters.q) p.set("q", filters.q);
      if (after) p.set("after", after);
      p.set("limit", "200");
      return `/admin/events?${p.toString()}`;
    },
    [deviceId, nodeId, filters.level, filters.kind, filters.q]
  );

  // First page whenever the scope or a server-side filter changes.
  useEffect(() => {
    if (!token) return;
    let stop = false;
    setState("loading");
    (async () => {
      const r = await http.get<EventPage>(token, query());
      if (stop) return;
      if (!r.ok) {
        setState(isNotFound(r.error) ? "missing" : "error");
        setError(r.error);
        return;
      }
      const list = r.result?.items ?? [];
      setItems(list);
      setHeld([]);
      setFresh(new Set());
      cursor.current = list[0]?.id ?? r.result?.nextAfter ?? null;
      setState("ready");
    })();
    return () => {
      stop = true;
    };
  }, [token, query]);

  // Live tail.
  useEffect(() => {
    if (!token || !live || state !== "ready") return;
    let busy = false;
    const iv = setInterval(async () => {
      if (busy) return;
      busy = true;
      const r = await http.get<EventPage>(token, query(cursor.current));
      busy = false;
      if (!r.ok) {
        if (isNotFound(r.error)) setState("missing");
        return;
      }
      const incoming = r.result?.items ?? [];
      if (r.result?.nextAfter) cursor.current = r.result.nextAfter;
      if (!incoming.length) return;
      cursor.current = incoming[0].id ?? cursor.current;
      if (pausedRef.current) {
        setHeld((h) => dedupe([...incoming, ...h]));
        return;
      }
      setItems((cur) => dedupe([...incoming, ...cur]).slice(0, MAX_ROWS));
      setFresh(new Set(incoming.map((e) => e.id)));
    }, POLL_MS);
    return () => clearInterval(iv);
  }, [token, live, state, query]);

  const resume = () => {
    setPaused(false);
    if (held.length) {
      setItems((cur) => dedupe([...held, ...cur]).slice(0, MAX_ROWS));
      setFresh(new Set(held.map((e) => e.id)));
      setHeld([]);
    }
  };

  // Client-side checks keep the view right even if the server ignores a filter; source is client-side only.
  const visible = useMemo(() => {
    const needle = filters.q.toLowerCase();
    return items.filter(
      (e) =>
        (!filters.level || e.level === filters.level) &&
        (!filters.source || e.source === filters.source) &&
        (!filters.kind || e.kind === filters.kind) &&
        (!needle || [e.message, e.aet, e.ip, e.kind, e.raw].some((v) => v?.toLowerCase().includes(needle)))
    );
  }, [items, filters]);

  const copyVisible = async () => {
    const lines = visible.map((e) =>
      [`${localDate(e.at)} ${clockMs(e.at)}`, e.level.toUpperCase().padEnd(5), e.source, e.kind, e.aet ?? "-", e.ip ?? "-", e.message].join("\t") + (e.raw ? `\n    raw: ${e.raw}` : "")
    );
    const head = `Jupiter event log${deviceId ? ` device=${deviceId}` : ""}${nodeId ? ` node=${nodeId}` : ""} (${visible.length} rows, times local ${Intl.DateTimeFormat().resolvedOptions().timeZone})`;
    if (await copyText([head, ...lines].join("\n"))) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  const toggle = (id: string) => setOpen((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });

  const controls = state !== "missing" && (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {live && state === "ready" && !paused && <ConnectionDots state="live" dots={3} size={3} label="live" sx={{ mr: 0.5, display: { xs: "none", sm: "flex" } }} />}
      <Tooltip title="Check for new events every 2.5 s">
        <Stack direction="row" alignItems="center">
          <Switch size="small" checked={live} onChange={(e) => { setLive(e.target.checked); if (!e.target.checked) resume(); }} inputProps={{ "aria-label": "Live" }} />
          <Typography sx={{ ...caps, fontSize: 10, color: live ? "secondary.main" : "text.secondary", mr: 1 }}>Live</Typography>
        </Stack>
      </Tooltip>
      {live && (
        <Button size="small" variant={paused ? "contained" : "text"} color={paused ? "secondary" : "inherit"} startIcon={paused ? <Play size={13} /> : <Pause size={13} />} onClick={() => (paused ? resume() : setPaused(true))}>
          {paused ? (held.length ? `Resume (${held.length} new)` : "Resume") : "Pause"}
        </Button>
      )}
      <Button size="small" color="inherit" startIcon={<ClipboardText size={14} />} onClick={copyVisible} disabled={!visible.length}>{copied ? "Copied" : "Copy"}</Button>
    </Stack>
  );

  return (
    <Panel title={title} icon={<ListMagnifyingGlass size={16} />} action={controls}>
      {state === "missing" ? (
        <NotYet>The event log is not available on this server yet. Once the Pandia reports, DICOM associations (connections from instruments), worklist queries, stores and TLS handshakes appear here as they happen.</NotYet>
      ) : (
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap">
            <TextField size="small" select label="Level" value={filters.level} onChange={(e) => setFilters({ ...filters, level: e.target.value as Filters["level"] })} sx={{ minWidth: 120 }}>
              <MenuItem value="">All</MenuItem>
              {(["info", "warn", "error"] as EventLevel[]).map((l) => <MenuItem key={l} value={l}><Stack direction="row" spacing={1} alignItems="center"><Dot color={levelColor[l]} /><span>{l}</span></Stack></MenuItem>)}
            </TextField>
            <TextField size="small" select label="Source" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value as Filters["source"] })} sx={{ minWidth: 120 }}>
              <MenuItem value="">All</MenuItem>
              {SOURCES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Kind" value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value })} sx={{ minWidth: 140 }}>
              <MenuItem value="">All</MenuItem>
              {KINDS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </TextField>
            <TextField
              size="small"
              label="Search"
              placeholder="AE title, IP, accession, message"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><MagnifyingGlass size={14} /></InputAdornment> }}
            />
          </Stack>
          {state === "error" && <Alert severity="error">{error}</Alert>}
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: "12px", overflow: "auto", maxHeight, bgcolor: "rgba(0,0,0,0.22)" }}>
            <Table size="small" stickyHeader sx={{ minWidth: 860, "& td": { py: 0.6, fontSize: 12.5 }, "& th": { bgcolor: tones.raised } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 28, px: 1 }} />
                  <TableCell sx={{ width: 104 }}>Time</TableCell>
                  <TableCell sx={{ width: 70 }}>Level</TableCell>
                  <TableCell sx={{ width: 130 }}>Source / kind</TableCell>
                  <TableCell sx={{ width: 130 }}>AE title</TableCell>
                  <TableCell sx={{ width: 120 }}>IP</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((e) => {
                  const isOpen = open.has(e.id);
                  return (
                    <Fragment key={e.id}>
                      <TableRow
                        hover
                        onClick={() => e.raw && toggle(e.id)}
                        sx={{
                          cursor: e.raw ? "pointer" : "default",
                          animation: fresh.has(e.id) ? `${flash} 2.4s ease-out` : "none",
                          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                          ...(isOpen ? { "& td": { borderBottom: 0 } } : {}),
                          ...(e.level === "error" ? { bgcolor: alpha(tones.fail, 0.05) } : {}),
                        }}
                      >
                        <TableCell sx={{ px: 1, color: "text.secondary" }}>{e.raw ? (isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />) : null}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title={new Date(e.at).toString()}><span><Mono sx={{ color: "text.secondary" }}>{clockMs(e.at)}</Mono></span></Tooltip>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Dot color={levelColor[e.level] ?? levelColor.info} size={5} />
                            <Box component="span" sx={{ color: e.level === "info" ? "text.secondary" : levelColor[e.level], fontSize: 11.5 }}>{e.level}</Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>{e.source}<Box component="span" sx={{ color: "text.disabled" }}> / </Box><Box component="span" sx={{ color: "text.primary" }}>{e.kind}</Box></TableCell>
                        <TableCell>{e.aet ? <Mono sx={{ color: "primary.light" }}>{e.aet}</Mono> : <Box component="span" sx={{ color: "text.disabled" }}>-</Box>}</TableCell>
                        <TableCell>{e.ip ? <Mono>{e.ip}</Mono> : <Box component="span" sx={{ color: "text.disabled" }}>-</Box>}</TableCell>
                        <TableCell sx={{ color: e.level === "error" ? tones.fail : "text.primary", wordBreak: "break-word" }}>{e.message}</TableCell>
                      </TableRow>
                      {isOpen && e.raw && (
                        <TableRow>
                          <TableCell />
                          <TableCell colSpan={6} sx={{ pt: 0, pb: 1.25 }}>
                            <Box component="pre" sx={{ m: 0, p: 1.25, borderRadius: "8px", bgcolor: "rgba(0,0,0,0.4)", border: 1, borderColor: "divider", fontFamily: fonts.mono, fontSize: 11.5, color: "#f3e4cf", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{e.raw}</Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
                {state === "loading" && !items.length && (
                  <TableRow><TableCell colSpan={7} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>Loading events.</TableCell></TableRow>
                )}
                {state === "ready" && !visible.length && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                      {items.length ? "No events match these filters." : live ? "No events yet. Waiting for the Pandia to report." : "No events yet."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {visible.length} of {items.length} shown, newest first. Times are local. Click a row with an arrow to show the raw log line.
          </Typography>
        </Stack>
      )}
    </Panel>
  );
}

function dedupe(list: JupiterEvent[]) {
  const seen = new Set<string>();
  return list.filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)));
}
